import { useEffect, useState } from "react";
import { api, fmtInt } from "@/lib/api";
import { CheckCircle, X, Trash } from "@phosphor-icons/react";

export default function ColorBreakdownModal({ orderId, item, itemIndex, onSaved, onClose, allItems }) {
  const skuPrefix = item.sku ? item.sku.toUpperCase().match(/^[A-Z]+/)?.[0] || "POP" : "POP";
  const [availableColors, setAvailableColors] = useState([]);
  const [loadingPalette, setLoadingPalette] = useState(true);
  const [stockByColor, setStockByColor] = useState({});
  const [breakdown, setBreakdown] = useState(item.color_breakdown || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoadingPalette(true);
    api.get("/cup-colors", { params: { type: skuPrefix } })
      .then(({ data }) => setAvailableColors(data))
      .catch(() => { /* silent — palette will be empty */ })
      .finally(() => setLoadingPalette(false));
    api.get("/inventory", { params: { types: skuPrefix } })
      .then(({ data }) => {
        const map = {};
        for (const row of data) map[row.color] = row.stock || 0;
        setStockByColor(map);
      })
      .catch(() => setStockByColor({}));
  }, [skuPrefix]);

  const totalCups = item.cups || 0;
  const allocated = breakdown.reduce((s, b) => s + (parseInt(b.qty) || 0), 0);
  const remaining = totalCups - allocated;

  const addColor = (colorName) => {
    if (breakdown.find((b) => b.color === colorName)) return;
    setBreakdown([...breakdown, { color: colorName, qty: Math.max(0, remaining) }]);
  };

  const updateQty = (i, qty) => {
    const c = [...breakdown];
    c[i] = { ...c[i], qty: parseInt(qty) || 0 };
    setBreakdown(c);
  };

  const removeRow = (i) => setBreakdown(breakdown.filter((_, j) => j !== i));

  const distributeEqually = () => {
    if (availableColors.length === 0) return;
    const per = Math.floor(totalCups / availableColors.length);
    const remainder = totalCups - per * availableColors.length;
    setBreakdown(availableColors.map((c, i) => ({ color: c.name, qty: per + (i < remainder ? 1 : 0) })));
  };

  const save = async () => {
    if (allocated !== totalCups) {
      setError(`Total distribuído (${allocated}) deve ser igual ao total de copos do item (${totalCups})`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const newItems = allItems.map((it, i) => ({
        sku: it.sku,
        name: it.name,
        qty: it.qty,
        unit_price: it.unit_price,
        color_breakdown: i === itemIndex
          ? breakdown.filter((b) => b.color && b.qty > 0)
          : (it.color_breakdown || []),
      }));
      const { data } = await api.put(`/orders/${orderId}/items`, { items: newItems });
      onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="glass max-w-3xl w-full p-6 md:p-8 my-4" onClick={(e) => e.stopPropagation()} data-testid="color-breakdown-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline text-zinc-500">CORES DO ITEM</div>
            <h2 className="font-head text-2xl font-black"><span className="font-mono">{item.sku}</span> · {item.name}</h2>
            <div className="text-sm text-zinc-500 mt-1">
              {item.qty}× · <strong className="text-black">{totalCups} copos</strong> · tipo <strong>{item.type}</strong>
            </div>
          </div>
          <button onClick={onClose}><X size={22} /></button>
        </div>

        <div className={`p-4 mb-4 border ${allocated === totalCups ? "border-[#00A300] bg-green-50" : "border-[#FFC000] bg-yellow-50"}`}>
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="overline text-zinc-500 mb-1">DISTRIBUÍDO</div>
              <div className="font-mono text-2xl font-bold">{fmtInt(allocated)} / {fmtInt(totalCups)}</div>
            </div>
            <div className="text-right">
              <div className="overline text-zinc-500 mb-1">RESTANTE</div>
              <div className={`font-mono text-2xl font-bold ${remaining === 0 ? "text-[#00A300]" : remaining > 0 ? "text-[#FFC000]" : "text-[#E60000]"}`}>{remaining > 0 ? "+" : ""}{remaining}</div>
            </div>
          </div>
        </div>

        <div className="border border-black bg-white p-4 mb-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="overline">PALETA · {skuPrefix}</div>
            <button type="button" className="text-xs underline" onClick={distributeEqually} data-testid="distribute-equally">Distribuir igualmente</button>
          </div>
          {loadingPalette ? (
            <div className="text-sm text-zinc-400 py-2">Carregando paleta…</div>
          ) : availableColors.length === 0 ? (
            <div className="text-sm text-zinc-500">
              Sem cores cadastradas para o tipo <strong>{skuPrefix}</strong>. <a className="underline" href="/cadastros/cup-colors" target="_blank" rel="noopener noreferrer">Cadastrar agora</a>.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableColors.map((c) => {
                const used = breakdown.find((b) => b.color === c.name);
                const stock = stockByColor[c.name];
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => addColor(c.name)}
                    disabled={!!used}
                    className={`flex items-center gap-2 px-3 py-2 border text-xs ${used ? "border-zinc-200 text-zinc-300 cursor-not-allowed" : "border-black hover:bg-zinc-100"}`}
                    data-testid={`palette-${c.name}`}
                  >
                    <span className="w-4 h-4 border border-black" style={{ background: c.hex || "#f4f4f5" }} />
                    {c.name}
                    {stock !== undefined && (
                      <span className={`font-mono text-[10px] px-1 border ${stock < 0 ? "border-[#E60000] text-[#E60000] font-bold" : "border-zinc-300 text-zinc-500"}`} title="Estoque disponível">
                        est. {stock}
                      </span>
                    )}
                    {used && <span className="font-mono">·{used.qty}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="border border-black bg-white mb-4">
          <div className="border-b border-black p-3"><div className="overline">DISTRIBUIÇÃO POR COR</div></div>
          {breakdown.length === 0 ? (
            <div className="p-8 text-center text-zinc-400 text-sm">Selecione cores da paleta acima.</div>
          ) : (
            <table className="table">
              <thead><tr><th>Cor</th><th className="text-center">Quantidade</th><th></th></tr></thead>
              <tbody>
                {breakdown.map((b, i) => {
                  const palette = availableColors.find((c) => c.name === b.color);
                  return (
                    <tr key={b.color || `bd-${i}`} data-testid={`breakdown-${i}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 border border-black" style={{ background: palette?.hex || "#f4f4f5" }} />
                          <span className="font-semibold">{b.color}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        <input type="number" min="0" className="input w-28 text-center" value={b.qty} onChange={(e) => updateQty(i, e.target.value)} data-testid={`bd-qty-${i}`} />
                      </td>
                      <td><button onClick={() => removeRow(i)} className="text-red-500"><Trash size={14} /></button></td>
                    </tr>
                  );
                })}
                <tr className="bg-black text-white">
                  <td className="text-right uppercase font-bold tracking-widest text-xs">TOTAL</td>
                  <td className="text-center font-mono font-bold text-lg">{fmtInt(allocated)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2 mb-3">{error}</div>}

        <div className="flex gap-3">
          <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn flex-1" onClick={save} disabled={saving || allocated !== totalCups} data-testid="save-breakdown">
            <CheckCircle size={16} weight="bold" /> {saving ? "Salvando..." : "Salvar Cores"}
          </button>
        </div>
      </div>
    </div>
  );
}
